import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chip, Icon, IconType, Tag, Tooltip,
} from '@routific/hive';
import styled from 'styled-components';

/**
 * Warns that an order completion (delivered/missed) was recorded too far from
 * the planned location. The Distance icon inherits the warning text color on
 * the tag/chip's own background (no custom badge).
 *
 * Each variant matches the surrounding UI on its surface:
 * - `icon` variant: an icon-only warning `Chip` (order detail panel attempt
 *   header).
 * - `label` variant: a "Completed far away" warning `Tag` (route detail panel
 *   order card, next to the order status which is also a `Tag`).
 *
 * Both variants share the same explanatory tooltip.
 */
type LocationExceptionWarningVariant = 'icon' | 'label';

interface Props {
  variant?: LocationExceptionWarningVariant;
}

const TooltipTarget = styled.span`
  display: inline-flex;
  align-items: center;
`;

// Collapse the (otherwise empty) MUI Chip label so the icon-only chip doesn't
// keep the label's horizontal padding as a trailing gap after the icon.
const iconOnlySx = {
  '& .MuiChip-label': { display: 'none' },
  '& .MuiChip-icon': { margin: 0 },
  paddingX: '4px',
};

const LocationExceptionWarning: React.FC<Props> = ({ variant = 'icon' }) => {
  const { t } = useTranslation('common');

  const label = t('orderDetails.locationException.label');
  const icon = <Icon icon={IconType.Distance} type="warning" size="small" />;

  const target = variant === 'label' ? (
    <Tag type="warning" size="small" icon={icon}>{label}</Tag>
  ) : (
    <Chip color="warning" size="small" icon={icon} aria-label={label} sx={iconOnlySx} />
  );

  return (
    <Tooltip
      title={t('orderDetails.locationException.tooltip')}
      placement="top"
      arrowPointAtCenter
    >
      <TooltipTarget data-testid="location-exception-warning">{target}</TooltipTarget>
    </Tooltip>
  );
};

export default LocationExceptionWarning;
